import { ALL_SCOPES } from './constants'
import { checkScope } from './utils/scope-utils'

export type McpToolRisk = 'read' | 'write' | 'destructive' | 'admin'

export interface McpToolScope {
  name: string
  label: string
  requiredScope: string
  requiredScopes?: readonly string[]
  group: string
  risk: McpToolRisk
  remediation: string
}

const ALL_SCOPE_SET = new Set<string>(ALL_SCOPES)
const SYNC_READ_SCOPES = ['source:read', 'sync:read'] as const
const SYNC_WRITE_SCOPES = ['source:upsert', 'sync:run'] as const

export const MCP_TOOL_SCOPES: readonly McpToolScope[] = [
  {
    name: 'document_create',
    label: 'Create document',
    requiredScope: 'doc:create',
    group: 'Documents',
    risk: 'write',
    remediation: 'Add doc:create or use the Editor agent preset.',
  },
  {
    name: 'document_get',
    label: 'Read document',
    requiredScope: 'doc:read',
    group: 'Documents',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'document_list',
    label: 'List documents',
    requiredScope: 'doc:read',
    group: 'Documents',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'document_update',
    label: 'Update document',
    requiredScope: 'doc:update',
    group: 'Documents',
    risk: 'write',
    remediation: 'Add doc:update or use the Editor agent preset.',
  },
  {
    name: 'document_delete',
    label: 'Delete document',
    requiredScope: 'doc:delete',
    group: 'Documents',
    risk: 'destructive',
    remediation: 'Add doc:delete only for trusted automation.',
  },
  {
    name: 'document_versions_list',
    label: 'List versions',
    requiredScope: 'doc:read',
    group: 'Documents',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'document_version_create',
    label: 'Create version',
    requiredScope: 'doc:update',
    group: 'Documents',
    risk: 'write',
    remediation: 'Add doc:update or use the Editor agent preset.',
  },
  {
    name: 'document_links_get',
    label: 'Read document links',
    requiredScope: 'doc:read',
    group: 'Documents',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'search',
    label: 'Search knowledge',
    requiredScope: 'doc:read',
    group: 'Search',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'graph_get',
    label: 'Read graph',
    requiredScope: 'doc:read',
    group: 'Graph',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'graph_traverse',
    label: 'Traverse graph',
    requiredScope: 'doc:read',
    group: 'Graph',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'graph_find_path',
    label: 'Find graph path',
    requiredScope: 'doc:read',
    group: 'Graph',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'graph_suggest_links',
    label: 'Suggest graph links',
    requiredScope: 'doc:read',
    group: 'Graph',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'graph_explain_connection',
    label: 'Explain graph connection',
    requiredScope: 'doc:read',
    group: 'Graph',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'graph_stats',
    label: 'Read graph stats',
    requiredScope: 'doc:read',
    group: 'Graph',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'folder_create',
    label: 'Create folder',
    requiredScope: 'folder:create',
    group: 'Folders',
    risk: 'write',
    remediation: 'Add folder:create or use the Editor agent preset.',
  },
  {
    name: 'folder_list',
    label: 'List folders',
    requiredScope: 'folder:read',
    group: 'Folders',
    risk: 'read',
    remediation: 'Add folder:read or use the Read-only agent preset.',
  },
  {
    name: 'folder_update',
    label: 'Update folder',
    requiredScope: 'folder:update',
    group: 'Folders',
    risk: 'write',
    remediation: 'Add folder:update or use the Editor agent preset.',
  },
  {
    name: 'folder_delete',
    label: 'Delete folder',
    requiredScope: 'folder:delete',
    group: 'Folders',
    risk: 'destructive',
    remediation: 'Add folder:delete only for trusted automation.',
  },
  {
    name: 'tag_list',
    label: 'List tags',
    requiredScope: 'doc:read',
    group: 'Tags',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'category_list',
    label: 'List categories',
    requiredScope: 'doc:read',
    group: 'Tags',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'upload_file',
    label: 'Upload file',
    requiredScope: 'storage:upload',
    group: 'Storage',
    risk: 'write',
    remediation: 'Add storage:upload or use the Storage worker preset.',
  },
  {
    name: 'upload_list',
    label: 'List uploads',
    requiredScope: 'storage:read',
    group: 'Storage',
    risk: 'read',
    remediation: 'Add storage:read or use the Storage worker preset.',
  },
  {
    name: 'search_images',
    label: 'Search images',
    requiredScope: 'doc:read',
    group: 'Search',
    risk: 'read',
    remediation: 'Add doc:read or use the Read-only agent preset.',
  },
  {
    name: 'member_list',
    label: 'List members',
    requiredScope: 'tenant:manage',
    group: 'Members',
    risk: 'admin',
    remediation: 'Member tools require tenant admin auth and cannot be granted to API keys today.',
  },
  {
    name: 'member_update_role',
    label: 'Update member role',
    requiredScope: 'user:manage',
    group: 'Members',
    risk: 'admin',
    remediation: 'Member tools require user admin auth and cannot be granted to API keys today.',
  },
  {
    name: 'member_remove',
    label: 'Remove member',
    requiredScope: 'user:manage',
    group: 'Members',
    risk: 'admin',
    remediation: 'Member tools require user admin auth and cannot be granted to API keys today.',
  },
  {
    name: 'api_key_create',
    label: 'Create API key',
    requiredScope: 'key:create',
    group: 'API Keys',
    risk: 'admin',
    remediation: 'Add key:create only to trusted key-management automation.',
  },
  {
    name: 'api_key_list',
    label: 'List API keys',
    requiredScope: 'key:read',
    group: 'API Keys',
    risk: 'read',
    remediation: 'Add key:read only to trusted key-management automation.',
  },
  {
    name: 'api_key_update',
    label: 'Update API key',
    requiredScope: 'key:update',
    group: 'API Keys',
    risk: 'admin',
    remediation: 'Add key:update only to trusted key-management automation.',
  },
  {
    name: 'api_key_revoke',
    label: 'Revoke API key',
    requiredScope: 'key:revoke',
    group: 'API Keys',
    risk: 'destructive',
    remediation: 'Add key:revoke only to trusted key-management automation.',
  },
  {
    name: 'share_link_create',
    label: 'Create share link',
    requiredScope: 'doc:share',
    group: 'Sharing',
    risk: 'write',
    remediation: 'Add doc:share or use a preset that can publish selected docs.',
  },
  {
    name: 'site_list',
    label: 'List hosted sites',
    requiredScope: 'site:read',
    group: 'Hosted Sites',
    risk: 'read',
    remediation: 'Add site:read or use the Storage worker preset.',
  },
  {
    name: 'site_get',
    label: 'Read hosted site',
    requiredScope: 'site:read',
    group: 'Hosted Sites',
    risk: 'read',
    remediation: 'Add site:read or use the Storage worker preset.',
  },
  {
    name: 'site_delete',
    label: 'Delete hosted site',
    requiredScope: 'site:delete',
    group: 'Hosted Sites',
    risk: 'destructive',
    remediation: 'Add site:delete only for trusted hosting automation.',
  },
  {
    name: 'docs_sync_status',
    label: 'Read docs sync status',
    requiredScope: 'source:read',
    requiredScopes: SYNC_READ_SCOPES,
    group: 'Source Sync',
    risk: 'read',
    remediation: 'Add source:read and sync:read or use the Docs sync agent preset.',
  },
  {
    name: 'source_conflict_list',
    label: 'List source conflicts',
    requiredScope: 'source:read',
    requiredScopes: SYNC_READ_SCOPES,
    group: 'Source Sync',
    risk: 'read',
    remediation: 'Add source:read and sync:read or use the Docs sync agent preset.',
  },
  {
    name: 'knowledge_upsert_source',
    label: 'Upsert source-backed document',
    requiredScope: 'source:upsert',
    requiredScopes: SYNC_WRITE_SCOPES,
    group: 'Source Sync',
    risk: 'write',
    remediation: 'Add source:upsert and sync:run or use the Docs sync agent preset.',
  },
  {
    name: 'knowledge_search',
    label: 'Search typed memory',
    requiredScope: 'memory:read',
    group: 'Agent Memory',
    risk: 'read',
    remediation: 'Add memory:read or use the Agent memory preset.',
  },
  {
    name: 'knowledge_read',
    label: 'Read typed memory',
    requiredScope: 'memory:read',
    group: 'Agent Memory',
    risk: 'read',
    remediation: 'Add memory:read or use the Agent memory preset.',
  },
  {
    name: 'memory_upsert',
    label: 'Upsert typed memory',
    requiredScope: 'memory:write',
    group: 'Agent Memory',
    risk: 'write',
    remediation: 'Add memory:write or use the Agent memory preset.',
  },
  {
    name: 'journal_append',
    label: 'Append journal memory',
    requiredScope: 'memory:journal',
    group: 'Agent Memory',
    risk: 'write',
    remediation: 'Add memory:journal or use the Agent memory preset.',
  },
] as const

export interface McpToolAccess {
  tool: McpToolScope
  available: boolean
  grantable: boolean
  missingScope?: string
  remediation?: string
}

export function isGrantableApiKeyScope(scope: string): boolean {
  return ALL_SCOPE_SET.has(scope)
}

export function getMcpToolAccess(scopes: readonly string[]): McpToolAccess[] {
  return MCP_TOOL_SCOPES.map((tool) => {
    const requiredScopes = tool.requiredScopes ?? [tool.requiredScope]
    const grantable = requiredScopes.every(isGrantableApiKeyScope)
    const missing = requiredScopes.find((scope) => !checkScope([...scopes], scope))
    const available = grantable && !missing
    return {
      tool,
      available,
      grantable,
      missingScope: available ? undefined : (missing ?? tool.requiredScope),
      remediation: available ? undefined : tool.remediation,
    }
  })
}

export function getMcpToolAccessSummary(scopes: readonly string[]) {
  const matrix = getMcpToolAccess(scopes)
  return {
    available: matrix.filter((item) => item.available),
    blocked: matrix.filter((item) => !item.available),
    ungrantable: matrix.filter((item) => !item.grantable),
  }
}
